import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import moment from "moment";
import {
  Search, Plus, Check, ArrowLeft, ArrowRight, Home, Building2, User,
  FlaskConical, MapPin, CalendarClock, ClipboardCheck, AlertCircle, Star, Trash2, Utensils,
} from "lucide-react";
import {
  getLabTests, findLabs, getPatients, getDoctors, createLabOrder,
} from "../../api/api";
import WizardSteps from "../../components/WizardSteps";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Skeleton } from "../../components/ui/skeleton";
import {
  COLLECTION_TYPE, COLLECTION_TYPE_LABEL, SAMPLE_TYPE_LABEL, formatCurrency,
} from "../../lib/lab-constants";
import { cn } from "../../lib/utils";

const ISO = "YYYY-MM-DD";

// Patient first, not tests first.
//
// The reference wizard opens on the catalogue, which means the desk builds a
// basket before knowing who it's for — and the patient's pincode, which
// decides which labs are even available, only surfaces at step four. Leading
// with the patient means their saved address pre-fills the location step and
// dead ends get caught early.
const STEPS = [
  { key: "patient", label: "Patient", icon: User },
  { key: "tests", label: "Tests", icon: FlaskConical },
  { key: "collection", label: "Collection", icon: MapPin },
  { key: "schedule", label: "Lab & Slot", icon: CalendarClock },
  { key: "review", label: "Review", icon: ClipboardCheck },
];

const EMPTY_PATIENT = { name: "", phone: "", email: "", gender: "Male", dob: "", address: { line: "", city: "", state: "", pincode: "" } };

export default function AddLabOrder() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // ---- patient -------------------------------------------------------------
  const [patientMode, setPatientMode] = useState("search"); // search | new
  const [patientQuery, setPatientQuery] = useState("");
  const [patientResults, setPatientResults] = useState([]);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [newPatient, setNewPatient] = useState(EMPTY_PATIENT);

  // ---- tests ---------------------------------------------------------------
  const [testQuery, setTestQuery] = useState("");
  const [category, setCategory] = useState("");
  const [tests, setTests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingTests, setLoadingTests] = useState(false);
  const [cart, setCart] = useState([]);

  // ---- collection ----------------------------------------------------------
  const [collectionType, setCollectionType] = useState(COLLECTION_TYPE.CENTER);
  const [pincode, setPincode] = useState("");
  const [addressLine, setAddressLine] = useState("");
  const [city, setCity] = useState("");

  // ---- schedule ------------------------------------------------------------
  const [labs, setLabs] = useState([]);
  const [labMeta, setLabMeta] = useState(null);
  const [loadingLabs, setLoadingLabs] = useState(false);
  const [slots, setSlots] = useState([]);
  const [selectedLab, setSelectedLab] = useState(null);
  const [date, setDate] = useState(moment().add(1, "day").format(ISO));
  const [slot, setSlot] = useState("");

  // ---- review --------------------------------------------------------------
  const [priority, setPriority] = useState(false);
  const [referredBy, setReferredBy] = useState("");
  const [referredDoctor, setReferredDoctor] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [notes, setNotes] = useState("");

  const cartCodes = useMemo(() => cart.map((t) => t.code), [cart]);
  const subtotal = cart.reduce((s, t) => s + (t.mrp || 0), 0);
  const fastingTests = cart.filter((t) => t.fasting_required);

  // ---------------------------------------------------------------- patients
  useEffect(() => {
    getDoctors({ limit: 200 })
      .then((res) => setDoctors(res.data || []))
      .catch(() => setDoctors([]));
  }, []);

  useEffect(() => {
    if (patientMode !== "search") return;
    const q = patientQuery.trim();
    if (q.length < 2) {
      setPatientResults([]);
      return;
    }
    setSearchingPatients(true);
    const t = setTimeout(() => {
      getPatients({ search: q, limit: 8 })
        .then((res) => setPatientResults(res.data || []))
        .catch(() => setPatientResults([]))
        .finally(() => setSearchingPatients(false));
    }, 250);
    return () => clearTimeout(t);
  }, [patientQuery, patientMode]);

  // ------------------------------------------------------------------- tests
  useEffect(() => {
    if (step !== 1) return;
    setLoadingTests(true);
    const t = setTimeout(() => {
      getLabTests({ search: testQuery || undefined, category: category || undefined, limit: 200 })
        .then((res) => {
          setTests(res.data || []);
          if (res.categories) setCategories(res.categories);
        })
        .catch((err) => toast.error(err?.message || "Could not load the test catalogue."))
        .finally(() => setLoadingTests(false));
    }, 250);
    return () => clearTimeout(t);
  }, [testQuery, category, step]);

  // -------------------------------------------------------------------- labs
  const loadLabs = useCallback(() => {
    if (!/^\d{6}$/.test(pincode) || !cartCodes.length) return;
    setLoadingLabs(true);
    setSelectedLab(null);
    findLabs({ pincode, collection_type: collectionType, test_codes: cartCodes.join(",") })
      .then((res) => {
        setLabs(res.data || []);
        setSlots(res.slots || []);
        setLabMeta(res.meta || null);
        if ((res.data || []).length === 1) setSelectedLab(res.data[0]);
      })
      .catch((err) => toast.error(err?.message || "Could not find labs."))
      .finally(() => setLoadingLabs(false));
  }, [pincode, collectionType, cartCodes]);

  useEffect(() => {
    if (step === 3) loadLabs();
  }, [step, loadLabs]);

  // --------------------------------------------------------------- cart ops
  const toggleTest = (test) => {
    setCart((prev) =>
      prev.some((t) => t.code === test.code)
        ? prev.filter((t) => t.code !== test.code)
        : [...prev, test]
    );
  };

  const choosePatient = (p) => {
    setSelectedPatient(p);
    // Their saved pincode pre-fills the location step — one less thing to ask.
    if (p.address?.pincode) setPincode(p.address.pincode);
    if (p.address?.line) setAddressLine(p.address.line);
    if (p.address?.city) setCity(p.address.city);
  };

  // ------------------------------------------------------------ step gating
  const stepValid = (i) => {
    switch (i) {
      case 0:
        return patientMode === "search"
          ? !!selectedPatient
          : newPatient.name.trim().length > 1 && newPatient.phone.trim().length >= 10;
      case 1:
        return cart.length > 0;
      case 2:
        return /^\d{6}$/.test(pincode) &&
          (collectionType === COLLECTION_TYPE.CENTER || addressLine.trim().length > 0);
      case 3:
        return !!selectedLab && !!date && !!slot;
      default:
        return true;
    }
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        test_codes: cartCodes,
        collection_type: collectionType,
        lab_id: selectedLab._id,
        pincode,
        address: { line: addressLine, city, state: "" },
        scheduled_date: date,
        scheduled_slot: slot,
        priority,
        referred_by: referredDoctor ? referredDoctor.name : referredBy,
        referred_by_doctor: referredDoctor?._id || null,
        notes,
      };
      if (patientMode === "search") {
        payload.patient_id = selectedPatient._id;
      } else {
        payload.patient = {
          ...newPatient,
          dob: newPatient.dob || null,
          address: { ...newPatient.address, pincode },
        };
      }
      const res = await createLabOrder(payload);
      toast.success(`Order ${res.data.order_no} placed.`);
      navigate("/lab-orders");
    } catch (err) {
      toast.error(err?.message || "Could not place the order.");
    } finally {
      setSubmitting(false);
    }
  };

  const patientName = patientMode === "search" ? selectedPatient?.name : newPatient.name;
  const patientPhone = patientMode === "search" ? selectedPatient?.phone : newPatient.phone;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/lab-orders")} aria-label="Back to lab orders">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h2 className="font-serif text-2xl font-semibold">New Lab Order</h2>
          <p className="text-sm text-muted-foreground">
            {STEPS[step].label} — step {step + 1} of {STEPS.length}
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-3">
        <WizardSteps steps={STEPS} current={step} onStepClick={setStep} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        {/* ------------------------------------------------------- main pane */}
        <div className="rounded-lg border border-border bg-card p-5">
          {/* ---- 0: patient ---- */}
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <h3 className="font-medium">Who is this order for?</h3>
                <p className="text-sm text-muted-foreground">
                  Search an existing patient so their consults and lab work stay on one record.
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant={patientMode === "search" ? "default" : "outline"}
                  size="sm"
                  className={cn(patientMode !== "search" && "bg-card")}
                  onClick={() => setPatientMode("search")}
                >
                  Existing patient
                </Button>
                <Button
                  variant={patientMode === "new" ? "default" : "outline"}
                  size="sm"
                  className={cn(patientMode !== "new" && "bg-card")}
                  onClick={() => { setPatientMode("new"); setSelectedPatient(null); }}
                >
                  <Plus className="h-3.5 w-3.5" /> New patient
                </Button>
              </div>

              {patientMode === "search" ? (
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      autoFocus
                      value={patientQuery}
                      onChange={(e) => setPatientQuery(e.target.value)}
                      placeholder="Search by name or mobile…"
                      className="pl-9"
                    />
                  </div>

                  {selectedPatient ? (
                    <div className="flex items-start justify-between rounded-md border border-primary/40 bg-primary/5 p-3">
                      <div>
                        <p className="font-medium">{selectedPatient.name}</p>
                        <p className="text-sm text-muted-foreground">{selectedPatient.phone}</p>
                        {selectedPatient.address?.city && (
                          <p className="text-xs text-muted-foreground">
                            {selectedPatient.address.city} {selectedPatient.address.pincode}
                          </p>
                        )}
                      </div>
                      <Button variant="link" className="h-auto p-0 text-sm" onClick={() => setSelectedPatient(null)}>
                        Change
                      </Button>
                    </div>
                  ) : searchingPatients ? (
                    <div className="space-y-2">
                      <Skeleton className="h-14 w-full" />
                      <Skeleton className="h-14 w-full" />
                    </div>
                  ) : patientQuery.trim().length < 2 ? (
                    <p className="rounded-md border border-dashed border-border bg-muted/30 px-3 py-6 text-center text-sm text-muted-foreground">
                      Type at least two characters to search.
                    </p>
                  ) : patientResults.length === 0 ? (
                    <div className="rounded-md border border-dashed border-border bg-muted/30 px-3 py-6 text-center">
                      <p className="text-sm text-muted-foreground">No patient matches “{patientQuery}”.</p>
                      <Button variant="outline" size="sm" className="mt-2 bg-card" onClick={() => {
                        setPatientMode("new");
                        setNewPatient((p) => ({ ...p, name: patientQuery }));
                      }}>
                        <Plus className="h-3.5 w-3.5" /> Create them instead
                      </Button>
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-md border border-border">
                      {patientResults.map((p) => (
                        <button
                          key={p._id}
                          type="button"
                          onClick={() => choosePatient(p)}
                          className="flex w-full items-center justify-between border-b border-border px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-muted/50"
                        >
                          <span>
                            <span className="block font-medium">{p.name}</span>
                            <span className="block text-xs text-muted-foreground">
                              {p.phone}{p.address?.pincode ? ` · ${p.address.pincode}` : ""}
                            </span>
                          </span>
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="np-name">Name *</Label>
                    <Input id="np-name" value={newPatient.name}
                      onChange={(e) => setNewPatient({ ...newPatient, name: e.target.value })}
                      placeholder="Patient name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="np-phone">Mobile *</Label>
                    <Input id="np-phone" value={newPatient.phone}
                      onChange={(e) => setNewPatient({ ...newPatient, phone: e.target.value })}
                      placeholder="10-digit mobile" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="np-email">Email</Label>
                    <Input id="np-email" type="email" value={newPatient.email}
                      onChange={(e) => setNewPatient({ ...newPatient, email: e.target.value })}
                      placeholder="Optional — reports are emailed here" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="np-gender">Gender</Label>
                    <select
                      id="np-gender"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                      value={newPatient.gender}
                      onChange={(e) => setNewPatient({ ...newPatient, gender: e.target.value })}
                    >
                      <option>Male</option><option>Female</option><option>Other</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="np-dob">Date of birth</Label>
                    <Input id="np-dob" type="date" value={newPatient.dob}
                      max={moment().format(ISO)}
                      onChange={(e) => setNewPatient({ ...newPatient, dob: e.target.value })} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---- 1: tests ---- */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <h3 className="font-medium">Select tests</h3>
                <p className="text-sm text-muted-foreground">
                  Prices shown are what {patientName || "the patient"} will be quoted.
                </p>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  autoFocus
                  value={testQuery}
                  onChange={(e) => setTestQuery(e.target.value)}
                  placeholder="Search by test name or code…"
                  className="pl-9"
                />
              </div>

              {categories.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  <Button
                    size="sm"
                    variant={!category ? "default" : "outline"}
                    className={cn("h-7 rounded-full px-3 text-xs", category && "bg-card")}
                    onClick={() => setCategory("")}
                  >
                    All
                  </Button>
                  {categories.map((c) => (
                    <Button
                      key={c}
                      size="sm"
                      variant={category === c ? "default" : "outline"}
                      className={cn("h-7 rounded-full px-3 text-xs", category !== c && "bg-card")}
                      onClick={() => setCategory(category === c ? "" : c)}
                    >
                      {c}
                    </Button>
                  ))}
                </div>
              )}

              {loadingTests ? (
                <div className="grid gap-2 sm:grid-cols-2">
                  {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
                </div>
              ) : tests.length === 0 ? (
                <p className="rounded-md border border-dashed border-border bg-muted/30 px-3 py-8 text-center text-sm text-muted-foreground">
                  No tests match that search.
                </p>
              ) : (
                <div className="grid max-h-[26rem] gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
                  {tests.map((t) => {
                    const selected = cartCodes.includes(t.code);
                    return (
                      <button
                        key={t._id}
                        type="button"
                        onClick={() => toggleTest(t)}
                        className={cn(
                          "flex items-start gap-2.5 rounded-md border p-3 text-left transition-colors",
                          selected ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"
                        )}
                      >
                        <span className={cn(
                          "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                          selected ? "border-primary bg-primary text-primary-foreground" : "border-input"
                        )}>
                          {selected && <Check className="h-3 w-3" strokeWidth={3} />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">{t.name}</span>
                          <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                            <span className="font-mono">{t.code}</span>
                            <Badge variant="outline" className="px-1.5 py-0 text-[10px] font-normal">
                              {SAMPLE_TYPE_LABEL[t.sample_type] || t.sample_type}
                            </Badge>
                            {t.fasting_required && (
                              <Badge variant="warning" className="gap-0.5 px-1.5 py-0 text-[10px] font-normal">
                                <Utensils className="h-2.5 w-2.5" /> Fasting
                              </Badge>
                            )}
                          </span>
                        </span>
                        <span className="shrink-0 text-sm font-medium tabular-nums">
                          {formatCurrency(t.mrp)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ---- 2: collection ---- */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <h3 className="font-medium">How is the sample collected?</h3>
                <p className="text-sm text-muted-foreground">
                  This decides which labs can take the order.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  { type: COLLECTION_TYPE.HOME, Icon: Home, blurb: "A phlebotomist visits the patient" },
                  { type: COLLECTION_TYPE.CENTER, Icon: Building2, blurb: "The patient visits the lab" },
                ].map(({ type, Icon, blurb }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setCollectionType(type)}
                    className={cn(
                      "flex flex-col items-center gap-2 rounded-lg border p-6 text-center transition-colors",
                      collectionType === type ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"
                    )}
                  >
                    <Icon className={cn("h-7 w-7", collectionType === type ? "text-primary" : "text-muted-foreground")} />
                    <span className="font-medium">{COLLECTION_TYPE_LABEL[type]}</span>
                    <span className="text-xs text-muted-foreground">{blurb}</span>
                  </button>
                ))}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="pincode">Pincode *</Label>
                  <Input
                    id="pincode"
                    inputMode="numeric"
                    maxLength={6}
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="6-digit pincode"
                  />
                  {pincode && !/^\d{6}$/.test(pincode) && (
                    <p className="text-xs text-destructive">Pincode must be exactly 6 digits.</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="city">City</Label>
                  <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
                </div>
              </div>

              {collectionType === COLLECTION_TYPE.HOME && (
                <div className="space-y-1.5">
                  <Label htmlFor="addr">Collection address *</Label>
                  <Input
                    id="addr"
                    value={addressLine}
                    onChange={(e) => setAddressLine(e.target.value)}
                    placeholder="House / street / landmark"
                  />
                  <p className="text-xs text-muted-foreground">
                    The phlebotomist needs a full address, not just a pincode.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ---- 3: lab & slot ---- */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <h3 className="font-medium">Choose a lab and collection window</h3>
                <p className="text-sm text-muted-foreground">
                  Showing labs that serve {pincode} and can run all {cart.length} selected test
                  {cart.length === 1 ? "" : "s"}.
                </p>
              </div>

              {loadingLabs ? (
                <div className="space-y-2">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : labs.length === 0 ? (
                <div className="space-y-3 rounded-md border border-dashed border-destructive/40 bg-destructive/5 p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                    <div>
                      <p className="text-sm font-medium text-destructive">No lab available</p>
                      <p className="text-sm text-muted-foreground">
                        {labMeta?.reason || "Nothing matches this combination."}
                      </p>
                    </div>
                  </div>
                  {/* A dead end with no way out is the worst part of the
                      reference flow — offer the actual fixes. */}
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" className="bg-card" onClick={() => setStep(2)}>
                      Change pincode
                    </Button>
                    <Button variant="outline" size="sm" className="bg-card" onClick={() => setStep(1)}>
                      Edit test selection
                    </Button>
                    {collectionType === COLLECTION_TYPE.HOME && (
                      <Button variant="outline" size="sm" className="bg-card"
                        onClick={() => { setCollectionType(COLLECTION_TYPE.CENTER); setStep(2); }}>
                        Try a center visit
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {labs.map((lab) => (
                    <button
                      key={lab._id}
                      type="button"
                      onClick={() => setSelectedLab(lab)}
                      className={cn(
                        "flex w-full items-start justify-between gap-3 rounded-md border p-3 text-left transition-colors",
                        selectedLab?._id === lab._id ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"
                      )}
                    >
                      <span className="min-w-0">
                        <span className="block font-medium">{lab.name}</span>
                        <span className="block text-xs text-muted-foreground">
                          {lab.address?.line}{lab.address?.city ? `, ${lab.address.city}` : ""}
                        </span>
                      </span>
                      {selectedLab?._id === lab._id && <Check className="h-4 w-4 shrink-0 text-primary" />}
                    </button>
                  ))}
                </div>
              )}

              {labs.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="lab-date">Collection date</Label>
                    <Input
                      id="lab-date"
                      type="date"
                      min={moment().format(ISO)}
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Time window</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {slots.map((s) => (
                        <Button
                          key={s}
                          type="button"
                          size="sm"
                          variant={slot === s ? "default" : "outline"}
                          className={cn("h-8 text-xs", slot !== s && "bg-card")}
                          onClick={() => setSlot(s)}
                        >
                          {s}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---- 4: review ---- */}
          {step === 4 && (
            <div className="space-y-4">
              <div>
                <h3 className="font-medium">Review before placing</h3>
                <p className="text-sm text-muted-foreground">
                  Nothing is saved until you confirm.
                </p>
              </div>

              {fastingTests.length > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 p-3">
                  <Utensils className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                  <p className="text-sm">
                    <span className="font-medium">Fasting required</span> — tell the patient to fast
                    8–12 hours before collection for: {fastingTests.map((t) => t.name).join(", ")}.
                  </p>
                </div>
              )}

              <dl className="divide-y divide-border rounded-md border border-border">
                {[
                  ["Patient", `${patientName} · ${patientPhone}`],
                  ["Collection", `${COLLECTION_TYPE_LABEL[collectionType]} · ${pincode}`],
                  ["Address", collectionType === COLLECTION_TYPE.HOME ? [addressLine, city].filter(Boolean).join(", ") : selectedLab?.address?.line || "—"],
                  ["Lab", selectedLab?.name],
                  ["Scheduled", `${moment(date, ISO).format("ddd, D MMM YYYY")} · ${slot}`],
                ].map(([k, v]) => (
                  <div key={k} className="flex gap-4 px-3 py-2.5 text-sm">
                    <dt className="w-28 shrink-0 text-muted-foreground">{k}</dt>
                    <dd className="min-w-0 flex-1 font-medium">{v || "—"}</dd>
                  </div>
                ))}
              </dl>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="ref">Referred by</Label>
                  {/* Picking an in-house doctor links the order to them, so it
                      appears in that doctor's activity log. Free text stays
                      available for outside referrers. */}
                  <select
                    id="ref"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={referredDoctor?._id || ""}
                    onChange={(e) => {
                      const d = doctors.find((x) => x._id === e.target.value) || null;
                      setReferredDoctor(d);
                      if (d) setReferredBy("");
                    }}
                  >
                    <option value="">External / not specified</option>
                    {doctors.map((d) => (
                      <option key={d._id} value={d._id}>{d.name}</option>
                    ))}
                  </select>
                  {!referredDoctor && (
                    <Input value={referredBy} onChange={(e) => setReferredBy(e.target.value)}
                      placeholder="Outside doctor or clinic (optional)" />
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="notes">Notes</Label>
                  <Input id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the lab should know" />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={priority} onCheckedChange={setPriority} />
                <Star className={cn("h-3.5 w-3.5", priority && "fill-warning text-warning")} />
                Mark as priority
              </label>
            </div>
          )}

          {/* ---- nav ---- */}
          <div className="mt-6 flex items-center justify-between border-t border-border pt-4">
            <Button variant="outline" className="bg-card" onClick={back} disabled={step === 0}>
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={next} disabled={!stepValid(step)}>
                Continue <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={submitting}>
                {submitting ? "Placing…" : `Place order · ${formatCurrency(subtotal)}`}
              </Button>
            )}
          </div>
        </div>

        {/* ------------------------------------------------------ summary rail */}
        <aside className="h-fit rounded-lg border border-border bg-card lg:sticky lg:top-4">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="font-medium">Order summary</h3>
            <Badge variant="outline">{cart.length}</Badge>
          </div>

          {patientName && (
            <div className="border-b border-border px-4 py-2.5">
              <p className="text-xs text-muted-foreground">Patient</p>
              <p className="truncate text-sm font-medium">{patientName}</p>
            </div>
          )}

          <div className="max-h-64 overflow-y-auto">
            {cart.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                No tests selected yet.
              </p>
            ) : (
              cart.map((t) => (
                <div key={t.code} className="flex items-start gap-2 border-b border-border px-4 py-2.5 last:border-0">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{t.name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {SAMPLE_TYPE_LABEL[t.sample_type] || t.sample_type}
                    </p>
                  </div>
                  <span className="shrink-0 text-sm tabular-nums">{formatCurrency(t.mrp)}</span>
                  <button
                    type="button"
                    onClick={() => toggleTest(t)}
                    aria-label={`Remove ${t.name}`}
                    className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>

          {cart.length > 0 && (
            <div className="space-y-1 border-t border-border px-4 py-3 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>Subtotal</span>
                <span className="tabular-nums">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between font-medium">
                <span>Total payable</span>
                <span className="tabular-nums">{formatCurrency(subtotal)}</span>
              </div>
              {fastingTests.length > 0 && (
                <p className="flex items-center gap-1.5 pt-1 text-xs text-warning">
                  <Utensils className="h-3 w-3" /> {fastingTests.length} test
                  {fastingTests.length === 1 ? "" : "s"} need fasting
                </p>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
